import { RcFile } from 'antd/es/upload';
import downloadFile from 'js-file-download';
import { AxiosResponse } from 'axios';
import i18next from 'i18next';

const APPLICATION_MIME_PREFIX = 'application/';
export const APPLICATION_JSON_MIME = `${APPLICATION_MIME_PREFIX}json`;

export const KB_FILE_SIZE = 1024;
export const MB_FILE_SIZE = KB_FILE_SIZE * KB_FILE_SIZE;
export const GB_FILE_SIZE = MB_FILE_SIZE * KB_FILE_SIZE;
export const TB_FILE_SIZE = GB_FILE_SIZE * KB_FILE_SIZE;
export const PB_FILE_SIZE = TB_FILE_SIZE * KB_FILE_SIZE;
export const EB_FILE_SIZE = PB_FILE_SIZE * KB_FILE_SIZE;
export const ZB_FILE_SIZE = EB_FILE_SIZE * KB_FILE_SIZE;
export const YB_FILE_SIZE = ZB_FILE_SIZE * KB_FILE_SIZE;

export const K_FILE_COUNT = 1000;
export const M_FILE_COUNT = K_FILE_COUNT * K_FILE_COUNT;
export const G_FILE_COUNT = M_FILE_COUNT * K_FILE_COUNT;

let FILE_SIZE_UNITS: { name: string; value: number }[] | undefined = undefined;
let FILE_COUNT_UNITS: { name: string; value: number }[] | undefined = undefined;

export const getBase64Content = (file: RcFile): Promise<string> =>
  new Promise<string>((res, rej) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        const base64Content = Buffer.from(e.target.result).toString('base64');
        res(base64Content);
      } else {
        rej("Can't upload a file.");
      }
    };
    reader.readAsArrayBuffer(file);
  });

export const base64ContentToFormFile = (file: RcFile, base64Content: string): File =>
  new File([new Blob([Buffer.from(base64Content, 'base64')])], file.name, {
    type: file.type || 'application/octet-stream',
  });

export const getFileExtension = (fileName: string): string => `.${fileName.split('.').pop()}`;

export const extractFileName = (response: AxiosResponse): string => {
  const disposition = response.headers['content-disposition'];

  if (!disposition) {
    return 'download';
  }

  // First try UTF-8 encoded filename (filename*=UTF-8''...)
  const utf8Match = disposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1]);
  }

  // Then try other encodings (filename*=ISO-8859-1''...)
  const encodedMatch = disposition.match(/filename\*=(.+?)''(.+?)(?:;|$)/i);
  if (encodedMatch) {
    return decodeURIComponent(encodedMatch[2]);
  }

  // Finally try regular ASCII filename
  const asciiMatch = disposition.match(/filename="?([^";\n]+)"?/i);
  if (asciiMatch) {
    return asciiMatch[1].trim();
  }

  return 'download';
};

export const downloadNamedFile = (response: AxiosResponse): void => {
  const name = extractFileName(response);
  downloadFile(response.data, name);
};

export const donwloadAsNamedJsonFile = (fileName: string, json: unknown): void => {
  const jsonString = JSON.stringify(json, null, 2);
  downloadFile(jsonString, fileName, APPLICATION_JSON_MIME);
};

export const formatFileValue = (value: number, units: { name: string; value: number }[]): string => {
  let index = 0;
  for (; index < units.length; index++) {
    const unit = units[index];
    if (value < unit.value * KB_FILE_SIZE) {
      break;
    }
  }

  if (index >= units.length) {
    index = units.length - 1;
  }

  const unit = units[index];
  const result = value / unit.value;
  const digits = result && !Number.isInteger(result) ? Math.max(0, 2 - Math.floor(Math.log10(result))) : 0;

  return `${result.toFixed(digits)}${unit.name}`;
};

export const formatFileSize = (size: number): string => {
  if (!FILE_SIZE_UNITS) {
    FILE_SIZE_UNITS = [
      { name: i18next.t('File: Size: B'), value: 1 },
      { name: i18next.t('File: Size: KB'), value: KB_FILE_SIZE },
      { name: i18next.t('File: Size: MB'), value: MB_FILE_SIZE },
      { name: i18next.t('File: Size: GB'), value: GB_FILE_SIZE },
      { name: i18next.t('File: Size: TB'), value: TB_FILE_SIZE },
      { name: i18next.t('File: Size: PB'), value: PB_FILE_SIZE },
      { name: i18next.t('File: Size: EB'), value: EB_FILE_SIZE },
      { name: i18next.t('File: Size: ZB'), value: ZB_FILE_SIZE },
      { name: i18next.t('File: Size: YB'), value: YB_FILE_SIZE },
    ];
  }

  return formatFileValue(size, FILE_SIZE_UNITS);
};

export const formatFileCount = (count: number): string => {
  if (!FILE_COUNT_UNITS) {
    FILE_COUNT_UNITS = [
      { name: i18next.t('File: Count: _'), value: 1 },
      { name: i18next.t('File: Count: K'), value: K_FILE_COUNT },
      { name: i18next.t('File: Count: M'), value: M_FILE_COUNT },
      { name: i18next.t('File: Count: G'), value: G_FILE_COUNT },
    ];
  }

  return formatFileValue(count, FILE_COUNT_UNITS);
};
